import type { BusinessSegmentConditions, SegmentConditionGroups } from '@reach/shared-types/conditions';
import type {
  AvailableFieldsResponse,
  AvailableFieldsResponseWithTypes,
} from '../engage-segments-tab/segment-definitions-api';
import styles from './EditSegment.module.css';
import { RecipientsPreview } from './RecipientsPreview';
import { SegmentDefinitionEditor } from './SegmentDefinitionEditor';
import { EditorModeToggle } from './visual-builder/EditorModeToggle';
import { SegmentDescriptionView } from './visual-builder/SegmentDescriptionView';
import { SegmentVisualBuilder } from './visual-builder/SegmentVisualBuilder';

export interface SegmentDefinitionSectionProps {
  formatType: 'legacy' | 'new';
  editorMode: 'builder' | 'json' | 'preview';
  onEditorModeChange: (mode: 'builder' | 'json' | 'preview') => void;
  contentsJson: string;
  onContentsChange: (json: string) => void;
  onValidationChange: (valid: boolean) => void;
  isJsonValid: boolean;
  isSaving: boolean;
  availableFields: AvailableFieldsResponse;
  isLoadingFields: boolean;
  fieldsError: Error | null;
  availableFieldsWithTypes: AvailableFieldsResponseWithTypes | undefined;
  isLoadingFieldsWithTypes: boolean;
  parsedConditions: BusinessSegmentConditions | null;
  parsedConditionGroups: SegmentConditionGroups | null;
  conditionGroupsForPreview: SegmentConditionGroups | null;
}

export function SegmentDefinitionSection({
  formatType,
  editorMode,
  onEditorModeChange,
  contentsJson,
  onContentsChange,
  onValidationChange,
  isJsonValid,
  isSaving,
  availableFields,
  isLoadingFields,
  fieldsError,
  availableFieldsWithTypes,
  isLoadingFieldsWithTypes,
  parsedConditions,
  parsedConditionGroups,
  conditionGroupsForPreview,
}: SegmentDefinitionSectionProps) {
  const editorDescriptionByMode = {
    builder: 'Build your segment with the visual builder',
    json: 'Define your segment using JSON condition groups',
    preview: 'Read your segment as a description outline',
  } as const;
  const editorDescription =
    formatType === 'legacy'
      ? 'Define your segment using JSON conditions'
      : editorDescriptionByMode[editorMode];

  return (
    <section className={styles.columns}>
      <article className={styles.editor} id="segment-editor-panel">
        <header>
          <h2>Segment Definition</h2>
          <p>{editorDescription}</p>
          {formatType === 'new' && (
            <EditorModeToggle mode={editorMode} onChange={onEditorModeChange} disabled={isSaving} />
          )}
        </header>
        {formatType === 'legacy' ? (
          <SegmentDefinitionEditor
            contentsJson={contentsJson}
            onContentsChange={onContentsChange}
            onValidationChange={onValidationChange}
            availableFields={availableFields}
            isLoadingFields={isLoadingFields}
            fieldsError={fieldsError}
            isReadOnly={isSaving}
            useLegacyFieldNames
            hideHeader
          />
        ) : editorMode === 'builder' ? (
          availableFieldsWithTypes ? (
            <SegmentVisualBuilder
              key="builder"
              conditionGroups={parsedConditionGroups ?? []}
              onChange={groups => onContentsChange(JSON.stringify(groups, null, 2))}
              availableFields={availableFieldsWithTypes}
              isLoading={isLoadingFieldsWithTypes}
            />
          ) : (
            <p className={styles.loadingState}>Loading fields...</p>
          )
        ) : editorMode === 'preview' ? (
          <SegmentDescriptionView conditionGroups={parsedConditionGroups ?? []} />
        ) : (
          <SegmentDefinitionEditor
            contentsJson={contentsJson}
            onContentsChange={onContentsChange}
            onValidationChange={onValidationChange}
            availableFields={availableFields}
            isLoadingFields={isLoadingFields}
            fieldsError={fieldsError}
            isReadOnly={isSaving}
            useLegacyFieldNames={false}
            hideHeader
          />
        )}
      </article>

      <RecipientsPreview
        conditions={formatType === 'legacy' ? parsedConditions : null}
        conditionGroups={formatType === 'new' ? conditionGroupsForPreview : null}
        isJsonValid={isJsonValid}
      />
    </section>
  );
}
